import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  PersonState,
  PersonSupplementaryInsuranceState,
  SupplementaryInsuranceTypes,
} from '../../../../../core/states/offer/offer.state';
import { getOrderedSupplementaryInsurances } from '../../../../../core/models/supplementary-insurance-order';
import { PersonService } from '../../../../../core/services/person.service';
import { Observable } from 'rxjs';
import { HospitalOfferType, Level } from '../../../../../core/api';
import { mapInsuranceIdForKey } from '../../../../../core/services/tracking/ecommerce/tracking-ecommerce-supplementary-insurance.map';

export interface BasketSupplementaryInsurance {
  productStateKey: SupplementaryInsuranceTypes;
  productTranslationKey: string;
  price?: number;
  level?: string;
  offerType?: HospitalOfferType;
}

@Component({
  standalone: false,
  selector: 'ssc-basket-person-detail',
  templateUrl: './basket-person-detail.component.html',
  styleUrls: ['./basket-person-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasketPersonDetailComponent implements OnChanges {
  @Input() public person: PersonState;
  @Input() public isOpen: boolean;
  @Input() public isSinglePerson: boolean;
  @Input() public isActivePerson: boolean;
  @Input() public isTupCustomer: boolean | null;
  @Output() public personToggled = new EventEmitter<void>();
  @Output() public toPersonClick = new EventEmitter<void>();
  @Output() public removeSupplementaryInsuranceClick = new EventEmitter<keyof PersonSupplementaryInsuranceState>();
  @Output() public removeBasicInsuranceClick = new EventEmitter<void>();

  public personName$: Observable<string>;
  public supplementaryInsurancesOfPerson: BasketSupplementaryInsurance[];

  constructor(private personService: PersonService) {}

  public onPersonToggled(): void {
    this.personToggled.emit();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.personName$ = this.personService.getFirstNameOrFallback$(this.person.details.firstName, this.person.personId);
    this.supplementaryInsurancesOfPerson = this.getSupplementaryInsurances();
  }

  private static camelCaseToScreamingSnakeCase(text: string): string {
    return text.replace(/[A-Z]/g, (letter) => `_${letter}`).toUpperCase();
  }

  private hasProp<K extends PropertyKey>(obj: unknown, prop: K): obj is Record<K, unknown> {
    return typeof obj === 'object' && obj !== null && prop in obj;
  }
  private getSupplementaryInsurances(): BasketSupplementaryInsurance[] {
    const supplementaryInsurance = this.person.supplementaryInsurances as PersonSupplementaryInsuranceState;

    return getOrderedSupplementaryInsurances(supplementaryInsurance).map((insurance) => {
      const key = insurance.key as keyof PersonSupplementaryInsuranceState;
      const product = supplementaryInsurance[key];
      return {
        productStateKey: key,
        productTranslationKey: BasketPersonDetailComponent.camelCaseToScreamingSnakeCase(key),
        price: insurance.insuranceState.price,
        level:
          this.hasProp(product, 'level') && key !== 'dental' // excludes dental etc if needed
            ? (product.level as Level)
            : undefined,
        offerType: this.hasProp(product, 'offerType') ? (product.offerType as HospitalOfferType) : undefined,
      };
    });
  }

  public getDetailedInsuranceNameTranslationKey(data: BasketSupplementaryInsurance): string {
    if (data.offerType) {
      const baseString = mapInsuranceIdForKey(data.productStateKey).toUpperCase();
      return `SUMMARY.SUPPLEMENTARY_INSURANCE.PRODUCT.${baseString}.CRITERIA.${baseString}_TYPE.${data.offerType}`;
    } else if (data.level) {
      return `SUPPLEMENTARY_INSURANCE_WITH_LEVEL_OVERLAY.${data.productTranslationKey}.${data.level}.TITLE`;
    }
    return 'COMMON.SUPPLEMENTARY_INSURANCE.PRODUCT.' + data.productTranslationKey;
  }
}
